<template>
    <div class="upsell-items" v-if="hasItems">
        <!-- Carton Item (handles both regular display and empty state internally) -->
        <CartonItem
            v-if="packaging !== undefined"
            :mode="mode"
            :currentCarton="packaging"
        />

        <!-- Flyer(s) - Loop through array -->
        <div
            v-for="(flyer, index) in currentFlyer"
            :key="`flyer-${index}`"
            class="flyer-wrapper"
            @click="openFlyerModal(index)"
        >
            <v-card
                class="upsell-item flyer-item clickable"
                elevation="2"
                :style="{ backgroundColor: flyer.bg }"
            >
                <div class="flyer-image-container">
                    <v-img
                        :src="flyer.url"
                        contain
                        class="product-image flyer-image"
                        height="100%"
                        width="100%"
                    >
                    </v-img>
                </div>
                <!-- Zoom Icon Overlay -->
                <div class="zoom-icon-overlay">
                    <v-icon color="white" size="20">mdi-magnify-plus</v-icon>
                </div>
            </v-card>

            <!-- Flyer Label Badge -->
            <div class="item-label-badge flyer-label-badge">
                <v-icon small color="white" class="mr-1">mdi-newspaper</v-icon>
                <span>FLYER {{ currentFlyer.length > 1 ? (index + 1) : '' }}</span>
            </div>
        </div>

        <!-- Media Modal for Flyers (always dark mode, no toggle) -->
        <MediaModal
            v-model="showMediaModal"
            :items="currentFlyer"
            :initialIndex="selectedFlyerIndex"
            mode="flyer"
            @close="closeMediaModal"
        />
        
        <!-- Upsell Items -->
        <div v-for="item in upsellItems" :key="item.sku" class="item-wrapper">
            <v-card class="upsell-item" elevation="2">
                <div class="item-image-container">
                    <v-img
                        :src="getImageUrl(item.sku)"
                        contain
                        class="product-image"
                        height="100%"
                        width="100%"
                    >
                    </v-img>
                    <!-- Quantity Badge positioned absolutely -->
                    <div v-if="item.qty > 1" class="qty-badge-overlay">
                        {{ item.qty }}x
                    </div>
                </div>
            </v-card>

            <!-- Upsell Item Badge -->
            <div class="item-label-badge upsell-badge">
                <span class="badge-text">{{ item.sku }}</span>
            </div>
        </div>
    </div>
</template>

<script>
import gravurfarbeImage from '@/assets/files/gravurfarbe_kinderbesteck.png'
import prioOrderImage from '@/assets/files/prio_order.png'
import CartonItem from './items/CartonItem.vue'
import MediaModal from './modals/MediaModal.vue'

export default {
    name: 'UpsellItems',
    components: {
        CartonItem,
        MediaModal
    },
    props: {
        upsellItems: {
            type: Array,
            default: () => []
        },
        currentFlyer: {
            type: Array,
            default: () => []
        },
        packaging: {
            type: Object,
            default: null
        },
        mode: {
            type: String,
            default: 'packlist'
        }
    },
    data() {
        return {
            gravurfarbeImage,
            prioOrderImage,
            showMediaModal: false,
            selectedFlyerIndex: 0
        }
    },
    computed: {
        hasItems() {
            // Show items if we have upsell items, flyer(s), or packaging info (CartonItem handles null internally)
            const result = this.upsellItems.length > 0 || (this.currentFlyer && this.currentFlyer.length > 0) || this.packaging !== undefined;
            console.log('📊 Upsell hasItems computed:', {
                upsellItemsLength: this.upsellItems.length,
                flyerCount: this.currentFlyer ? this.currentFlyer.length : 0,
                hasPackaging: !!this.packaging,
                packagingUndefined: this.packaging === undefined,
                result: result
            });
            return result;
        }
    },
    methods: {
        getImageUrl(sku) {
            switch (sku) {
                case 'gravurfarbe_kinderbesteck':
                    return gravurfarbeImage;
                case 'PRIO_ORDER':
                    return prioOrderImage;
                default:
                    return `/api/files/sku?sku=${sku}`;
            }
        },
        openFlyerModal(index) {
            console.log('📸 Opening flyer modal at index:', index);
            this.selectedFlyerIndex = index;
            this.showMediaModal = true;
        },
        closeMediaModal() {
            console.log('❌ Closing media modal');
            this.showMediaModal = false;
        }
    },
    watch: {
        currentFlyer: {
            handler(newVal, oldVal) {
                console.log('🎭 Upsell - currentFlyer prop changed (array):', { old: oldVal, new: newVal });
                if (newVal && newVal.length > 0) {
                    console.log(`📋 ${newVal.length} flyer(s) received:`);
                    newVal.forEach((flyer, index) => {
                        console.log(`  Flyer ${index + 1}:`, {
                            url: flyer.url,
                            bg: flyer.bg,
                            color: flyer.color,
                            label: flyer.label,
                            value: flyer.value
                        });
                    });
                } else {
                    console.log('⚠️ No flyer data received');
                }
            },
            immediate: true
        },
        upsellItems: {
            handler(newVal) {
                console.log('Upsell - upsellItems changed:', newVal?.length || 0, 'items');
            },
            immediate: true
        }
    }
}
</script>

<style scoped>
.upsell-items {
    position: sticky;
    top: 0;
    z-index: 100;
    background-color: #f8f9fa;
    padding: 16px;
    padding-bottom: 28px;
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    border-bottom: 1px solid #e0e0e0;
}

/* Item Wrappers - Contains card and badge */
.flyer-wrapper,
.item-wrapper {
    position: relative;
    width: 100px;
    height: 100px;
    flex-shrink: 0;
    flex-grow: 0;
}

.upsell-item {
    width: 100px;
    height: 100px;
    background-color: #fff;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    flex-grow: 0;
    aspect-ratio: 1/1;
    position: relative;
    overflow: hidden;
}

/* Flyer-specific styling */
.upsell-item.flyer-item {
    border: 2px solid rgba(0, 0, 0, 0.2);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Clickable flyer items */
.upsell-item.clickable {
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
}

.upsell-item.clickable:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
    border-color: rgba(25, 118, 210, 0.5);
}

.upsell-item.clickable:active {
    transform: scale(0.98);
}

/* Zoom Icon Overlay */
.zoom-icon-overlay {
    position: absolute;
    bottom: 4px;
    right: 4px;
    background: rgba(0, 0, 0, 0.6);
    border-radius: 50%;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
}

.upsell-item.clickable:hover .zoom-icon-overlay {
    opacity: 1;
}

/* Image Containers - ensures proper border-radius clipping */
.flyer-image-container,
.item-image-container {
    width: 100%;
    height: 100%;
    overflow: hidden;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Images - Add padding for white space */
.product-image.flyer-image,
.item-image-container .product-image {
    padding: 12px;
}

/* Item Label Badges - positioned relative to wrapper */
.item-label-badge {
    position: absolute;
    bottom: -12px;
    left: 50%;
    transform: translateX(-50%);
    color: white;
    padding: 4px 12px;
    border-radius: 2px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.5px;
    z-index: 10;
    display: flex;
    align-items: center;
    white-space: nowrap;
    border: 1px solid rgba(255, 255, 255, 0.3);
    max-width: 90px;
    overflow: hidden;
}

/* Badge Text - truncate with ellipsis */
.badge-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Flyer Badge - Blue gradient */
.flyer-label-badge {
    background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
    box-shadow: 0 2px 8px rgba(25, 118, 210, 0.4);
}

/* Upsell Badge - Purple gradient */
.upsell-badge {
    background: linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%);
    box-shadow: 0 2px 8px rgba(155, 89, 182, 0.4);
}

/* Quantity Badge Overlay - positioned on top right of image */
.qty-badge-overlay {
    position: absolute;
    top: 4px;
    right: 4px;
    background-color: #f44336;
    color: white;
    font-size: 12px;
    font-weight: 700;
    padding: 4px 8px;
    border-radius: 4px;
    z-index: 10;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.qty-badge {
    border-radius: 4px;
    background-color: #f44336;
    color: white;
}

.qty-badge.qty-high {
    background-color: #d32f2f;
    color: white;
    animation: blinkRed 1.5s ease-in-out infinite; /* Faster blink for high quantities */
}

@keyframes blinkRed {
    0% {
        background-color: #f44336;
        box-shadow: 0 0 5px rgba(244, 67, 54, 0.5);
    }
}

/* Tablet-specific adjustments (768px - 1366px) */
@media (min-width: 768px) and (max-width: 1366px) {
    .upsell-items {
        padding: 12px;
        padding-bottom: 22px;
        gap: 6px;
    }

    .flyer-wrapper,
    .item-wrapper,
    .upsell-item {
        width: 80px;
        height: 80px;
    }

    .item-label-badge {
        bottom: -10px;
        padding: 3px 10px;
        font-size: 9px;
        max-width: 72px;
    }

    .product-image.flyer-image {
        padding: 8px;
    }
}
</style>